import { Dispatch, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useField } from 'react-final-form';
import spacetime from 'spacetime';
import cn from 'classnames';
import debounce from 'lodash.debounce';

import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IDictionaryProfKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryProfKindSelectOption';
import { KNM_INFO } from 'CorePackage/MainModule/routes/routes';
import {
  FEDERAL_LAW_BLOCK_ID,
  KNO_ORGANIZATION_BLOCK_ID,
  LOCATION_BLOCK_ID,
  OFFICIAL_FULL_NAME_BLOCK_ID,
  PM_FORM_BLOCK_ID,
  PM_SIGNER_POSITION_BLOCK_ID,
  PM_TYPE_BLOCK_ID,
  START_DATE_BLOCK_ID,
  STOP_DATE_BLOCK_ID,
  SUPERVISION_TYPE_BLOCK_ID,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmErknmProfKindsSelectOptions,
  getKnmErknmSupervisionTypesSelectOptions,
  getKnmFormRef,
  getKnmIsNew,
  getKnmErknmPositionsSelectOptions,
  getKnmSystemType,
  getKnmDataMassDelete,
  getKnmDataMassCancel,
  getKnmIsNeedActSaveOrSign,
  getKnmIsErpStatusNew,
  getKnmData,
  getKnmDateReasonChangeKno,
  getKnmOldKnoOrganization,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  getDictionariesFederalLawsSelectOptions,
  getDictionariesKnoOrganizationsSelectOptions,
  getKnoSupervisionStartDate,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IPm } from 'APIPackage/KnmModule/interfaces/IPm';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { Row } from 'UIPackage/FormModule/components/Row';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { IKnmErknmFormSuspensionEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { PmSuspensionEvents } from 'KnmPmPackage/PmSuspensionEventsModule/components/PmSuspensionEvents';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { IInteractionCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { VideoCallInfo } from 'KnmErknmPackage/ErknmSubjectModule/components/VideoCallInfo/VideoCallInfo';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';

import { IKnmSignatureInfo } from '../../../APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { SelectInputFieldAutoOptions } from '../../../UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { KnmErknmStatusCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import {
  getAccountOrganizationsData,
  getAccountProsecutorOrganizationsWithDistrictSelectOptions,
} from '../../../CorePackage/AccountModule/selectors/accountSelectors';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import {
  knmInfoGetWorkDaysCountBeforeStart,
  knmSetSupervisionType,
} from '../../../KnmPackage/MainModule/actions/knmActions';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { Tooltip } from '../../../UIPackage/MainModule/components/Tooltip';
import { IKnmPmFormRescheduling } from '../../MainModule/interfaces/IKnmPmFormRescheduling';
import { TitleSectionElements } from '../../../KnmErknmPackage/ErknmInfoModule/components/TitleSectionElements';
import { IKnmAbrogationForm } from '../../../APIPackage/KnmModule/interfaces/IKnmAbrogationForm';
import { IUserOrganisationSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { PmRescheduling } from './PmRescheduling/PmRescheduling';
import styles from './PmInfo.module.scss';
import { PmAbrogation } from './PmAbrogaion/PmAbrogation';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';
import { MassDeleteInfo } from '../../../KnmPackage/MainModule/components/MassDeleteInfo';
import { IKnmMassDelete } from '../../../APIPackage/KnmModule/interfaces/IKnmMassDelete';
import { MassCancelInfo } from '../../../KnmPackage/MainModule/components/MassCancelInfo';
import { IKnmMassCancel } from '../../../APIPackage/KnmModule/interfaces/IKnmMassCancel';
import { ExternalSystemSignInfoBlock } from '../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { Signers } from '../../../KnmPackage/SignersModule/components/Signers';
import { useKnoNameChangeText } from '../../../KnmPackage/MainModule/hooks/useKnmNameChangeHintText';
import { Hint } from '../../../CorePackage/MainModule/components/Hint';

interface IPmInfoProps {
  abrogation: IKnmAbrogationForm | null | undefined;
  accessToken?: IPm['accessToken'];
  className?: string;
  created: string | undefined;
  erpId?: IKnm['erpId'];
  eventsPm: IKnmErknmFormEvent[] | undefined;
  extension: IKnmPmFormExtension | undefined;
  interactionCategory: IInteractionCategorySelectOption | undefined;
  isWatchOnly: boolean;
  originalStopDate: string | undefined;
  pmType: IDictionaryProfKindSelectOption | undefined;
  publicPortalURL: string;
  rescheduling: IKnmPmFormRescheduling[] | null | undefined;
  signatureInfo: IKnmSignatureInfo[] | null | undefined;
  signed: boolean | null | undefined;
  statusCode: IKnmStatus['code'] | undefined;
  suspensionEvents: IKnmErknmFormSuspensionEvent[] | undefined;
}

interface IPmInfoStoreProps {
  federalLawsSelectOptions: IDictionarySelectOption<IFederalLawErvk>[] | undefined;
  formRef: IKnmFormRef | undefined;
  isErknm: boolean;
  isErpStatusNew: boolean;
  isNeedActSaveOrSign?: boolean;
  isNewKnm: boolean;
  isSupervisor: boolean;
  knmRawData: IKnm | undefined;
  knmSystemType?: KnmSystemTypesEnum;
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  knoSupervisionStartDate: string | null | undefined;
  massCancelData: IKnmMassCancel | null | undefined;
  massDeleteData: IKnmMassDelete | null | undefined;
  pmTypesSelectOptions: IDictionarySelectOption<IProfKind>[] | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  settings: IAdminSetting[] | undefined;
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  userKnoOrganisations: IUserOrganisation[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  dateReasonChangeKno: string | null | undefined;
  oldKnoOrganization: string | null | undefined;
}

const getDaysLabel = (num: number) => num2str(num, ['день', 'дня', 'дней']);
const getWorkDaysLabel = (num: number) => (num % 10 === 1 ? 'рабочий' : 'рабочих');

const PmInfoWithoutStore = ({
  abrogation,
  accessToken,
  className = '',
  created,
  erpId,
  eventsPm,
  extension,
  interactionCategory,
  isWatchOnly,
  originalStopDate,
  pmType,
  publicPortalURL,
  rescheduling,
  signatureInfo,
  signed,
  statusCode,
  suspensionEvents,
  federalLawsSelectOptions,
  formRef,
  isErknm,
  isErpStatusNew,
  isNeedActSaveOrSign = false,
  isNewKnm,
  isSupervisor,
  knmRawData,
  knmSystemType,
  knoOrganizationsSelectOptions,
  knoSupervisionStartDate,
  massCancelData,
  massDeleteData,
  pmTypesSelectOptions,
  positionsSelectOptions,
  settings,
  supervisionTypesSelectOptions,
  userKnoOrganisations,
  userProsecutorOrganisationsSelectOptions,
  dateReasonChangeKno,
  oldKnoOrganization,
}: IPmInfoProps & IPmInfoStoreProps) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isKnoOrganizationDisabled, setIsKnoOrganizationDisabled] = useState<boolean>(false);
  const suspensionEvent = useMemo(() => suspensionEvents?.[suspensionEvents.length - 1], [suspensionEvents]);
  const periodExtensionAmountDays = extension?.guid ? Number(extension?.amountDays) : undefined;
  const periodSuspensionDays = suspensionEvent?.suspensionDays;

  const isEnableSuspensionTooltip = periodSuspensionDays !== undefined || periodExtensionAmountDays;

  const isTypeOP = pmType?.code === ProfKindCodesEnum.OP;
  const isTypePP = pmType?.code === ProfKindCodesEnum.PP;

  const isPmFormWatchOnly: boolean =
    isWatchOnly ||
    isNeedActSaveOrSign ||
    !isSupervisor ||
    (!isNewKnm &&
      isErknm &&
      statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS &&
      statusCode !== KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT);

  const isValid4121Cert = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_21_CERT_STARTS_FROM,
  );

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const isSignerInfoDisabled =
    isWatchOnly ||
    !isSupervisor ||
    (!isErpStatusNew && !isNewKnm && statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS);

  const handleClearReason = useCallback(() => {
    const reasonsPm = formRef?.current?.getState().values?.reasonsPm;

    if (Array.isArray(reasonsPm) && reasonsPm.length > 0) {
      reasonsPm.forEach((item, index) => {
        formRef?.current.change(`reasonsPm[${index}].ervkUuid` as keyof IKnmPmForm);
        formRef?.current.change(`reasonsPm[${index}].type` as keyof IKnmPmForm);
      });

      formRef?.current.change('isUnableToRefuse');
    }
  }, [formRef]);

  const handleDownloadButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const canvas = qrRef.current?.lastChild as HTMLCanvasElement;
      const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      saveFile(pngUrl, `${erpId ?? 'qr'}.png`);
    },
    [erpId],
  );

  const handleKnoOrganizationChange = useCallback(() => {
    formRef?.current.change('supervisionTypePm');
    formRef?.current.change('pmType');
    formRef?.current.change('isFiz');
    handleClearReason();
  }, [formRef]);

  const handleSupervisionTypeChange = useCallback(
    (value: IDictionarySupervisionTypeSelectOption | undefined) => {
      if (value) {
        formRef?.current.change('supervisionTypePm', {
          ...value,
          label: value.selectedLabel || value.label,
        });
        dispatch(knmSetSupervisionType());
      } else {
        formRef?.current.change('supervisionTypePm');
      }

      formRef?.current.change('pmType');
      formRef?.current.change('isFiz');
      handleClearReason();
    },
    [formRef, dispatch],
  );

  const handlePmTypeChange = useCallback(
    (value: IDictionaryProfKindSelectOption | undefined) => {
      formRef?.current.change('pmForm');
      handleClearReason();
      formRef?.current.change('officialFullName');
      formRef?.current.change('location');
      if (value?.code === ProfKindCodesEnum.OP) {
        formRef?.current.change('stopDate');
      }
    },
    [formRef],
  );

  const startDateLabel = isTypeOP ? 'Дата объявления предостережения' : 'Дата начала';

  const isUseKnoSupervisorStartDate = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_36_STARTS_FROM,
  );

  const minStartDate =
    isUseKnoSupervisorStartDate && knoSupervisionStartDate
      ? new Date(formatDate({ date: knoSupervisionStartDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }))
      : undefined;

  const knoOrganizationPm = formRef?.current.getFieldState('knoOrganizationPm')?.value;

  const knoOrganizationPmLabel: string | undefined = useField('knoOrganizationPm', {
    subscription: { value: true },
  }).input.value?.label;
  const knoNameChangeText = useKnoNameChangeText(knoOrganizationPmLabel, oldKnoOrganization, dateReasonChangeKno);

  /**
   * useEffect для поиска и выбора предзаполненного значения для "Контрольный (надзорный) орган"
   */

  useEffect(() => {
    if (userKnoOrganisations && knoOrganizationsSelectOptions && !knoOrganizationPm) {
      const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

      if (byDefaultKno) {
        const knoOrg = knoOrganizationsSelectOptions.find(i => i.idStr?.toString() === byDefaultKno.id);

        if (isErknm) {
          if (knoOrg && isNewKnm) {
            setTimeout(() => formRef?.current.change('knoOrganizationPm', knoOrg), 300);
            setIsKnoOrganizationDisabled(true);
          }
        }
      }
    }
  }, [formRef, userKnoOrganisations, knoOrganizationsSelectOptions, knoOrganizationPm, isNewKnm]);

  useEffect(() => {
    if (userProsecutorOrganisationsSelectOptions && isNewKnm) {
      const byDefault = userProsecutorOrganisationsSelectOptions.find(i => i.byDefault);

      if (byDefault) {
        formRef?.current.change('prosecutorOrganizationErknm', byDefault);
      }
    }
  }, [
    formRef,
    userKnoOrganisations,
    knoOrganizationsSelectOptions,
    knoOrganizationPm,
    isNewKnm,
    userProsecutorOrganisationsSelectOptions,
  ]);

  /**
   * Функция очистки поля "Основание"
   */
  const onClearReason = useCallback(() => {
    if (statusCode === KnmErknmStatusCodesEnum.FILL_IN_PROCESS) {
      formRef?.current.change('eventsPm', []);
      handleClearReason();
    }
  }, [formRef, statusCode]);

  const handleGetWorkDaysCountBeforeStart = useCallback((startDate: string) => {
    const formattedDate = formatDate({ date: startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS });

    const formDate = spacetime(formattedDate || '');
    const currentDate = spacetime();

    if (formDate.isAfter(currentDate) || formDate.isSame(currentDate, 'date')) {
      dispatch(knmInfoGetWorkDaysCountBeforeStart(formattedDate));
    }
  }, []);

  const debouncedHandleGetWorkDaysCountBeforeStart = useCallback(debounce(handleGetWorkDaysCountBeforeStart, 500), [
    handleGetWorkDaysCountBeforeStart,
  ]);

  const isLaterThen411 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.USE_REMOTE_MONITORING_PM_4_1_1_STARTS_FROM,
  );

  const isShowInteractionCategory = isLaterThen411 && isErknm;

  /**
   * Обработчик выбора поля "Основание"
   */
  const handleInteractionCategoryChange = useCallback(() => {
    formRef?.current.change(`videoCallInfo`);

    const events = formRef?.current.getFieldState('eventsPm')?.value;

    if (events) {
      events.forEach((item, index) => {
        formRef.current.change(`eventsPm[${index}].interactionCategory` as keyof IKnmPmForm);
      });
    }
  }, [formRef]);

  const signersRender = () => {
    if (isUseSigners && isErknm) {
      return (
        <Signers
          formRef={formRef}
          hintText="ФИО должно совпадать со сведениями из ЭЦП"
          inspectors={undefined}
          isPm={true}
          label="Подписанты решения"
          parentId={''}
          parentName={''}
          positionsSelectOptions={positionsSelectOptions}
        />
      );
    }

    return (
      <Row>
        {isTypePP && knmRawData?.pmErknm?.preventiveVisitSystemSignatureUsed ? (
          <ExternalSystemSignInfoBlock
            systemSignatureDateTime={knmRawData.pmErknm.preventiveVisitSystemSignatureDateTime as string}
            systemSignatureTitle={knmRawData.pmErknm.preventiveVisitSystemSignatureTitle as string}
          />
        ) : (
          <>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: isErknm ? 'ФИО должностного лица' : 'ФИО должностного лица, подписавшего решение',
                field: `officialFullName`,
                isPm: true,
              })}
              color="black"
              size="half"
            >
              <TextInputField
                hadToBeHowInSignMessage={
                  !isValid4121Cert || isSignerInfoDisabled ? null : 'ФИО должно совпадать со сведениями из ЭЦП'
                }
                id={OFFICIAL_FULL_NAME_BLOCK_ID}
                name="officialFullName"
                disabled={isSignerInfoDisabled}
              />
            </FieldBlock>
            <FieldBlock
              label={getTitleFieldWithRedStar({
                label: isErknm ? 'Должность лица, подписавшего паспорт ПМ' : 'Должность лица, подписавшего решение',
                field: `pmSignerPosition`,
                isPm: true,
              })}
              color="black"
              size="half"
            >
              <SelectInputField
                className={styles.PositionField}
                name={`pmSignerPosition`}
                id={PM_SIGNER_POSITION_BLOCK_ID}
                placeholder="Выберите должность"
                options={positionsSelectOptions}
                isSearchable
                disabled={isSignerInfoDisabled}
                size="medium"
              />
            </FieldBlock>
          </>
        )}
      </Row>
    );
  };

  return (
    <>
      <div className={className ? `${styles.Wrapper} ${className}` : styles.Wrapper} id={KNM_INFO}>
        <div className={styles.TitleContainer}>
          <h2 className="shared-title__with-margin">Сведения о профилактическом мероприятии</h2>
          {accessToken && (
            <TitleSectionElements
              isSigned={signed}
              signatureInfo={signatureInfo}
              publicPortalURL={publicPortalURL}
              qrRef={qrRef}
              accessToken={accessToken}
              handleDownloadButton={handleDownloadButton}
            />
          )}
        </div>

        <div className={styles.KnoOrganizationFieldBlock} id={KNO_ORGANIZATION_BLOCK_ID}>
          <div className="shared-group-margin">
            <label className="shared-group-label" htmlFor="knoOrganizationPm">
              Контрольный (надзорный) орган
            </label>
          </div>
          <SelectInputField
            id="knoOrganizationPm"
            name="knoOrganizationPm"
            options={knoOrganizationsSelectOptions}
            isClearable
            isSearchable
            disabled={Boolean(statusCode) || isWatchOnly || !isNewKnm || isKnoOrganizationDisabled}
            size="medium"
            onChange={handleKnoOrganizationChange}
          />
          {knoNameChangeText && <Hint margin="top" text={knoNameChangeText} />}
        </div>

        {!isErknm && (
          <div className={styles.FederalLawFieldBlock} id={FEDERAL_LAW_BLOCK_ID}>
            <div className={`${'shared-group-margin'} ${styles.federalLawGroup}`}>
              <label className="shared-group-label" htmlFor="federalLaw">
                Федеральный закон, в соответствии с которым проводится мероприятие
              </label>
              <SelectInputField
                className={styles.FederalLawErpSelectField}
                id="federalLaw"
                name="federalLaw"
                options={federalLawsSelectOptions}
                disabled={Boolean(statusCode) || isWatchOnly}
                size="medium"
              />
            </div>
          </div>
        )}

        <div className={styles.KindControlFieldBlock} id={SUPERVISION_TYPE_BLOCK_ID}>
          <div className="shared-group-margin">
            <label className="shared-group-label" htmlFor="supervisionTypePm">
              {getTitleFieldWithRedStar({
                label: 'Вид государственного контроля (надзора)',
                field: `supervisionTypePm`,
                isPm: true,
              })}
            </label>
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Федеральный закон, в соответствии с которым проводится мероприятие</p>
                    <p>Контрольный (надзорный) орган</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          </div>
          <SelectInputField
            id="supervisionTypePm"
            name="supervisionTypePm"
            options={supervisionTypesSelectOptions}
            isSearchable
            disabled={true}
            size="medium"
            onChange={handleSupervisionTypeChange}
          />
        </div>

        <div className={styles.KindKnmFieldBlock} id={PM_TYPE_BLOCK_ID}>
          <div className="shared-group-margin">
            <label className="shared-group-label" htmlFor="pmType">
              {getTitleFieldWithRedStar({
                label: 'Вид профилактического мероприятия',
                field: `pmType`,
                isPm: true,
              })}
            </label>
            <Tooltip
              placement="right"
              type="info"
              content={
                <div className="shared-tooltip-info-content">
                  Поле зависит от значения в следующем поле:
                  <ul>
                    <p>Контрольный (надзорный) орган</p>
                    <p>Вид контроля (надзора)</p>
                  </ul>
                </div>
              }
            >
              <span className="shared-tooltip-info-icon" />
            </Tooltip>
          </div>
          <SelectInputField
            id="pmType"
            name="pmType"
            options={pmTypesSelectOptions}
            disabled={Boolean(statusCode) || isWatchOnly || isNewKnm}
            size="medium"
            onChange={handlePmTypeChange}
          />
        </div>

        <div className={cn('shared-group-label', styles.DatesTitle)}>Даты проведения профилактического мероприятия</div>

        <div className={cn('shared-field-row', styles.StartDateFieldBlock)} id={START_DATE_BLOCK_ID}>
          <div className="shared-field-row-block_width">
            <label className="shared-field-label_row" htmlFor="startDate">
              {getTitleFieldWithRedStar({
                label: startDateLabel,
                field: `startDate`,
                isPm: true,
              })}
            </label>
          </div>
          <DatePickerField
            className="shared-row-field"
            id="startDate"
            name="startDate"
            disabled={isWatchOnly || !isNewKnm || !isSupervisor || !isErknm}
            minDate={minStartDate}
            onChange={date => {
              onClearReason();
              date && debouncedHandleGetWorkDaysCountBeforeStart(date.toString());
              onClearReason();
            }}
          />
        </div>

        {isTypePP && (
          <div className={cn('shared-field-row', styles.StopDateFieldBlock)} id={STOP_DATE_BLOCK_ID}>
            <div className="shared-field-row-block_width">
              <label className="shared-field-label_row" htmlFor="stopDate">
                {getTitleFieldWithRedStar({
                  label: 'Дата окончания',
                  field: `stopDate`,
                  isPm: true,
                })}
              </label>
            </div>
            <Row align="center">
              <DatePickerField
                className="shared-row-field"
                id="stopDate"
                name="stopDate"
                disabled={
                  isWatchOnly ||
                  !isSupervisor ||
                  (statusCode && statusCode !== KnmErknmStatusCodesEnum.FILL_IN_PROCESS) ||
                  !isErknm ||
                  !isNewKnm
                }
                onChange={isNewKnm ? undefined : onClearReason}
              />
              {isEnableSuspensionTooltip && (
                <Tooltip
                  placement="topRight"
                  type="info"
                  content={
                    <div className="shared-tooltip-info-content">
                      <ul>
                        <p>Исходная дата окончания: {originalStopDate}</p>
                        {typeof periodExtensionAmountDays === 'number' && (
                          <p>
                            Продлена на {periodExtensionAmountDays} {getWorkDaysLabel(periodExtensionAmountDays)}&nbsp;
                            {getDaysLabel(periodExtensionAmountDays)}
                          </p>
                        )}
                        {typeof periodSuspensionDays === 'number' && (
                          <p>
                            Приостановлена на {periodSuspensionDays}
                            {extension?.isWorkDaysOnly ? ` ${getWorkDaysLabel(periodSuspensionDays)} ` : ' '}
                            {getDaysLabel(periodSuspensionDays)}
                          </p>
                        )}
                      </ul>
                    </div>
                  }
                >
                  <span className="shared-tooltip-info-icon" />
                </Tooltip>
              )}
            </Row>
          </div>
        )}

        <MassCancelInfo massCancel={massCancelData} />
        <MassDeleteInfo massDelete={massDeleteData} />

        {isTypePP && (
          <div className={styles.PmFormFieldBlock} id={PM_FORM_BLOCK_ID}>
            {isShowInteractionCategory ? (
              <>
                <div className="shared-group-margin">
                  <label className="shared-group-label" htmlFor="interactionCategory">
                    {getTitleFieldWithRedStar({
                      label:
                        'Сведения об использовании в рамках ПМ средств дистанционного взаимодействия с контролируемыми лицами',
                      sectionName: 'interactionCategory',
                      field: `interactionCategory`,
                      isPm: true,
                    })}
                  </label>
                </div>
                <SelectInputFieldAutoOptions
                  className={styles.InteractionCategorySelect}
                  id="interactionCategory"
                  name="interactionCategory"
                  isClearable={true}
                  size="large"
                  disabled={isPmFormWatchOnly || isNeedActSaveOrSign}
                  optionName={CommonCatalogsNamesEnum.INTERACTION_CATEGORIES}
                  placeholder={getTitleFieldWithRedStar({
                    label: 'Выберите категорию взаимодействия',
                    field: `interactionCategory`,
                    isPm: true,
                  })}
                  onChange={handleInteractionCategoryChange}
                />
              </>
            ) : (
              <>
                <div className="shared-group-margin">
                  <label className="shared-group-label" htmlFor="pmForm">
                    Форма проведения профилактического мероприятия
                  </label>
                </div>
                <SelectInputFieldAutoOptions
                  id="pmForm"
                  name="pmForm"
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.PM_FORMS_CONDUCTING}
                  disabled={isPmFormWatchOnly}
                />
              </>
            )}
          </div>
        )}

        {isShowInteractionCategory && interactionCategory && interactionCategory.id !== 3 && (
          <VideoCallInfo isRequiredSubject={true} isPm rescheduling={rescheduling} />
        )}

        {isTypePP && (
          <div className={styles.PmLocationFieldBlock} id={LOCATION_BLOCK_ID}>
            <div className="shared-group-margin">
              <label className="shared-group-label" htmlFor="location">
                {getTitleFieldWithRedStar({
                  label: 'Место проведения профилактического мероприятия',
                  field: `location`,
                  isPm: true,
                })}
              </label>
            </div>
            <KnmAddressField
              id="location"
              addressFieldPathWithPrefix="location"
              resize="none"
              formRef={formRef}
              disabled={isPmFormWatchOnly}
              prefixNameNotInFias={''}
              prefixNameMatchesObjectAddress={''}
              knmSystemType={knmSystemType}
              manualAddressPlaceholder={'Место'}
            />
            <div className="shared-group-margin" />
            {signersRender()}
          </div>
        )}

        <PmSuspensionEvents
          className={className}
          watchOnly={isWatchOnly}
          eventsPm={eventsPm}
          suspensionEvents={suspensionEvents}
        />
      </div>

      <PmRescheduling created={created} pmType={pmType} rescheduling={rescheduling} isWatchOnly={isWatchOnly} />

      {isTypePP && !massCancelData?.cancelled && <PmAbrogation abrogation={abrogation} isWatchOnly={isWatchOnly} />}
    </>
  );
};

const mapStateToProps = (state: IRootState): IPmInfoStoreProps => {
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

  return {
    federalLawsSelectOptions: getDictionariesFederalLawsSelectOptions(state),
    formRef: getKnmFormRef(state),
    isErknm,
    isErpStatusNew: getKnmIsErpStatusNew(state),
    isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
    isNewKnm: getKnmIsNew(state) || false,
    isSupervisor: getUserRoleIsSupervisor(state),
    knmRawData: getKnmData(state),
    knmSystemType: getKnmSystemType(state),
    knoOrganizationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
    knoSupervisionStartDate: getKnoSupervisionStartDate(state),
    massCancelData: getKnmDataMassCancel(state),
    massDeleteData: getKnmDataMassDelete(state),
    pmTypesSelectOptions: getKnmErknmProfKindsSelectOptions(state),
    positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
    settings: getAdminFetchSettings(state),
    supervisionTypesSelectOptions: getKnmErknmSupervisionTypesSelectOptions(state),
    userKnoOrganisations: getAccountOrganizationsData(state).kno,
    userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsWithDistrictSelectOptions(state),
    dateReasonChangeKno: getKnmDateReasonChangeKno(state),
    oldKnoOrganization: getKnmOldKnoOrganization(state),
  };
};

export const PmInfo = connect(mapStateToProps)(PmInfoWithoutStore);
